import { fmtDate, fmtDateTime } from "@/lib/dateFormat";
// Prompt CL1 / CL1b — shared claim labels, badge helpers and the browser photo shrinker.
// CL1b Part B: every visible word is short and plain. Internal status codes never change.
export const STATUS_LABELS = {
  draft: "Draft",
  late_pending_admin: "Late – Admin to Allow",
  submitted: "Sent for Check",
  under_review: "Being Checked",
  video_call_requested: "Video Call Asked",
  pending_final_approval: "Waiting for Admin",
  more_info_requested: "Need More Proof",
  cn_pending: "Make CN",
  cn_awaiting_approval: "CN Waiting for Admin",
  cn_approved: "CN Ready to Send",
  closed_settled: "Closed – CN Sent",
  closed_rejected: "Closed – Rejected",
};

export const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700",
  late_pending_admin: "bg-amber-100 text-amber-800",
  submitted: "bg-blue-100 text-blue-700",
  under_review: "bg-blue-100 text-blue-700",
  video_call_requested: "bg-indigo-100 text-indigo-700",
  pending_final_approval: "bg-violet-100 text-violet-700",
  more_info_requested: "bg-amber-100 text-amber-800",
  cn_pending: "bg-teal-100 text-teal-800",
  cn_awaiting_approval: "bg-teal-50 text-teal-700",
  cn_approved: "bg-emerald-100 text-emerald-700",
  closed_settled: "bg-emerald-100 text-emerald-700",
  closed_rejected: "bg-red-100 text-red-700",
};

// CL1b A1 — the one warning line Ravi asked for, word for word.
export const EMAIL_NOT_SENT_TEXT =
  "Email NOT sent — please send the CN to the customer by WhatsApp or your own email.";

export const statusLabel = (s) => STATUS_LABELS[s] || pretty(s);
export const pretty = (s) =>
  s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";
export const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
export const when = (iso) => (iso ? fmtDateTime(iso) : "—");
export const dayOnly = (iso) => (iso ? fmtDate(iso) : "—");

// ── CL1b A6 — light photos ────────────────────────────────────────────────
export const PHOTO_HARD_LIMIT = 2 * 1024 * 1024; // after shrinking
export const PHOTO_TARGET = 500 * 1024;
export const VIDEO_LIMIT = 50 * 1024 * 1024;

export const sizeText = (bytes) =>
  Number(bytes) >= 1024 * 1024
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(Number(bytes || 0) / 1024))} KB`;

const PHOTO_NAME = /\.(jpe?g|png|webp)$/i;
export const isPhotoFile = (file) => PHOTO_NAME.test(file?.name || "") || /^image\//i.test(file?.type || "");

/**
 * Shrink a photo inside the browser so the phone never uploads 8 MB.
 * Longest side 1600 px, JPEG quality ~0.75, aiming for <= 500 KB. Keeps the file name.
 */
export async function compressPhoto(file, maxSide = 1600, quality = 0.75, target = PHOTO_TARGET) {
  if (!isPhotoFile(file)) return file;
  let img;
  try {
    img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = rej;
      el.src = URL.createObjectURL(file);
    });
  } catch {
    return file; // cannot decode — let the server decide
  }
  const longest = Math.max(img.width, img.height) || 1;
  const scale = Math.min(1, maxSide / longest);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);

  let q = quality;
  let blob = null;
  for (let i = 0; i < 4; i += 1) {
    /* eslint-disable no-await-in-loop */
    blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
    if (!blob || blob.size <= target) break;
    q = Math.max(0.4, q - 0.15);
  }
  if (!blob || blob.size >= file.size) return file;
  return new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() });
}

// Plain-English timeline lines (6.3).
export function eventHeadline(ev) {
  const who = ev.actor || "System";
  const map = {
    created: `${who} raised the claim`,
    evidence_added: `${who} added photos or videos`,
    submitted: `${who} sent the claim for check`,
    late_flagged: "Marked as a late claim",
    late_override: `${who} decided on the late claim`,
    video_call_requested: `${who} asked for a video call`,
    video_call_done: `${who} marked the video call as done`,
    comment: `${who} wrote a note`,
    reviewer_decision: `${(ev.data || {}).is_preliminary ? "Preliminary" : "Reviewer"} decision by ${who}`,
    sent_to_admin: `${who} sent the claim to Admin`,
    admin_more_info: `${who} asked for more proof`,
    reviewer_response: `${who} sent it back to Admin`,
    final_decision: `Admin decision by ${who}`,
    cn_linked: `${who} made the credit note`,
    cn_approved: "Admin approved the credit note",
    cn_rejected: "Credit note rejected or removed",
    cn_emailed: `${who} sent the credit note by email`,
    email_failed: "Email NOT sent",
    cn_marked_sent: `${who} sent the credit note by hand`,
    closed: "Claim closed",
    reopened: `${who} opened the claim again`,
    sla_breach: "Time limit crossed",
    escalated: "ESCALATED — time limit crossed twice",
    settings_snapshot: "Claim rules captured",
  };
  return map[ev.type] || pretty(ev.type);
}
